// KIND_META — the per-kind catalogue. The single source of truth that teaches
// the AI (purpose / good-for / layouts / pairings), drives the Studio pickers
// (layouts, backgrounds, change-type), and declares each kind's editable region
// manifest. Co-located with slide-model.ts; dumped to JSON for the Go AI
// contract (Phase 6). Adding/renaming here is the one place kinds are described.
//
// `regions` lists the FIXED editable region ids a kind always emits; `itemRegions`
// documents the array-indexed patterns (the `*` is the 0-based index). Both must
// match what render-slides.ts emits — guarded by a test (the rot guard).

import type { SlideKind, BgType } from './slide-model';

export interface LayoutDef {
  id: string;        // layout id written to slide.layout + the .lay-<id> class
  label: string;     // human label for the Studio picker
  blurb?: string;    // one line for the AI / tooltip
}

export interface KindMeta {
  purpose: string;            // what this kind is for (AI + docs)
  goodForData: string;        // the content/data it represents well (AI)
  regions: string[];          // fixed editable region ids (always emitted)
  itemRegions: string[];      // array-indexed region patterns (e.g. 'gal-*-t')
  layouts: LayoutDef[];       // layouts[0] = the current/default look
  backgrounds: BgType[];      // background types this kind may take
  defaultLayout: string;
  defaultBackground: BgType;
  pairsWith: SlideKind[];     // kinds that theme well alongside this one
  fontRole: 'display' | 'body';
}

const T = (id: string, label: string, blurb?: string): LayoutDef => ({ id, label, blurb });

export const KIND_META: Record<SlideKind, KindMeta> = {
  cover: {
    purpose: 'Open the deck — the title moment.',
    goodForData: 'Deck title, subtitle, author/date; sets the tone.',
    regions: ['eyebrow', 'title', 'sub', 'byline', 'foot'],
    itemRegions: [],
    layouts: [T('classic', 'Classic', 'Top-left title flow'), T('centred', 'Centred'), T('statement', 'Statement', 'Huge centred title'), T('editorial', 'Editorial', 'Serif title treatment'), T('with-image', 'With image', 'Title over a full background'), T('side-panel', 'Side panel', '60/40 split with media'), T('split-portrait', 'Split portrait', 'Title left, tall media panel right (~45%)'), T('colour-band', 'Colour band')],
    backgrounds: ['none', 'colour', 'gradient', 'image', 'image-split', 'motion', 'video'],
    defaultLayout: 'classic', defaultBackground: 'gradient',
    pairsWith: ['agenda', 'stat-row', 'cta'], fontRole: 'display',
  },
  'full-bleed': {
    purpose: 'A statement moment — one image, one line.',
    goodForData: 'A section break or a single dramatic claim over imagery.',
    regions: ['eyebrow', 'title', 'lede'],
    itemRegions: [],
    layouts: [T('overlay-bottom', 'Overlay (bottom)'), T('overlay-centre', 'Overlay (centre)'), T('side-panel', 'Side panel')],
    backgrounds: ['gradient', 'image', 'image-split', 'motion', 'colour', 'video'],
    defaultLayout: 'overlay-bottom', defaultBackground: 'gradient',
    pairsWith: ['cover', 'quote-image', 'cta'], fontRole: 'display',
  },
  'quote-image': {
    purpose: 'A testimonial over imagery.',
    goodForData: 'A customer/leader quote with attribution.',
    regions: ['quote', 'name', 'meta', 'avatar'],
    itemRegions: [],
    layouts: [T('portrait-overlay', 'Portrait overlay'), T('centred-mark', 'Centred mark'), T('side-quote', 'Side quote')],
    backgrounds: ['gradient', 'image', 'image-split', 'motion'],
    defaultLayout: 'portrait-overlay', defaultBackground: 'gradient',
    pairsWith: ['quote', 'full-bleed', 'cta'], fontRole: 'display',
  },
  quote: {
    purpose: 'A testimonial on the page (no image).',
    goodForData: 'A short, high-impact quote with attribution.',
    regions: ['eyebrow', 'title', 'quote', 'name', 'meta', 'avatar', 'footer'],
    itemRegions: [],
    layouts: [T('centred', 'Centred'), T('with-portrait', 'With portrait'), T('editorial-serif', 'Editorial serif'), T('billboard', 'Billboard', 'Oversized centred pull-quote, minimal chrome')],
    backgrounds: ['none', 'colour', 'gradient'],
    defaultLayout: 'centred', defaultBackground: 'none',
    pairsWith: ['quote-image', 'stat-row', 'cta'], fontRole: 'display',
  },
  split: {
    purpose: 'Image one side, content the other.',
    goodForData: 'A point that benefits from a supporting image + a few lines.',
    regions: ['eyebrow', 'title', 'lede', 'caption'],
    itemRegions: ['bullet-*'],
    layouts: [T('image-left', 'Image left'), T('image-right', 'Image right'), T('image-top', 'Image top', 'Media band on top, copy below'), T('wide-media', 'Wide media'), T('caption-over', 'Caption over')],
    backgrounds: ['image-split', 'gradient', 'image', 'colour', 'video'],
    defaultLayout: 'image-left', defaultBackground: 'image-split',
    pairsWith: ['features', 'bullets', 'gallery'], fontRole: 'body',
  },
  gallery: {
    purpose: 'A showcase of several images with captions.',
    goodForData: '2–4 images/screens with short captions.',
    regions: ['eyebrow', 'title', 'footer'],
    itemRegions: ['gal-*-t', 'gal-*-n'],
    layouts: [T('grid-3', 'Grid of 3'), T('grid-2', 'Grid of 2'), T('mosaic', 'Mosaic'), T('filmstrip', 'Filmstrip')],
    backgrounds: ['none', 'colour', 'gradient'],
    defaultLayout: 'grid-3', defaultBackground: 'none',
    pairsWith: ['split', 'features', 'cover'], fontRole: 'body',
  },
  agenda: {
    purpose: 'The running order of the deck.',
    goodForData: 'A numbered list of sections with one-line notes.',
    regions: ['eyebrow', 'title', 'footer'],
    itemRegions: ['ag-*-t', 'ag-*-n'],
    layouts: [T('numbered-list', 'Numbered list'), T('two-col', 'Two columns'), T('cards', 'Cards', 'A grid of agenda cards'), T('with-image', 'With image'), T('ledger', 'Ledger')],
    backgrounds: ['none', 'colour', 'gradient'],
    defaultLayout: 'numbered-list', defaultBackground: 'none',
    pairsWith: ['cover', 'bullets', 'features'], fontRole: 'body',
  },
  'stat-row': {
    purpose: 'A row of hero figures.',
    goodForData: '2–4 headline numbers with short labels.',
    regions: ['eyebrow', 'title', 'lede', 'footer'],
    itemRegions: ['stat-*-n', 'stat-*-l'],
    layouts: [T('row', 'Row'), T('centred', 'Centred'), T('stacked', 'Stacked', 'A hero stat over a supporting row'), T('colour-band', 'Colour band'), T('ledger', 'Ledger')],
    backgrounds: ['none', 'colour', 'gradient'],
    defaultLayout: 'row', defaultBackground: 'none',
    pairsWith: ['bignum', 'kpi', 'trend'], fontRole: 'display',
  },
  bignum: {
    purpose: 'One figure that matters.',
    goodForData: 'A single dominant number + a one-line claim + a delta.',
    regions: ['eyebrow', 'title', 'lede', 'value', 'vlabel', 'delta', 'footer'],
    itemRegions: [],
    layouts: [T('centred', 'Centred'), T('split-context', 'Split context'), T('band', 'Band')],
    backgrounds: ['none', 'colour', 'gradient'],
    defaultLayout: 'centred', defaultBackground: 'none',
    pairsWith: ['stat-row', 'trend', 'kpi'], fontRole: 'display',
  },
  kpi: {
    purpose: 'A dashboard of metrics.',
    goodForData: '2–4 KPIs with value, delta and a sparkline.',
    regions: ['eyebrow', 'title', 'lede', 'footer'],
    itemRegions: ['kpi-*-label', 'kpi-*-value', 'kpi-*-delta'],
    layouts: [T('grid-2x2', 'Grid 2×2'), T('row', 'Row'), T('spotlight', 'Spotlight'), T('with-image', 'With image')],
    backgrounds: ['none', 'colour', 'gradient'],
    defaultLayout: 'grid-2x2', defaultBackground: 'none',
    pairsWith: ['stat-row', 'trend', 'chart'], fontRole: 'body',
  },
  trend: {
    purpose: 'A number over time.',
    goodForData: 'A time series — the line going the right way.',
    regions: ['eyebrow', 'title', 'lede', 'trend-src'],
    itemRegions: ['trend-*-x'],
    layouts: [T('area', 'Area'), T('area-callout', 'Area + callout'), T('minimal', 'Minimal')],
    backgrounds: ['none', 'colour'],
    defaultLayout: 'area', defaultBackground: 'none',
    pairsWith: ['chart', 'bignum', 'kpi'], fontRole: 'body',
  },
  chart: {
    purpose: 'Compare values across categories.',
    goodForData: 'A small set of labelled bars.',
    regions: ['eyebrow', 'title', 'lede', 'footer'],
    itemRegions: ['chart-*-k'],
    layouts: [T('bars', 'Bars'), T('bars-source-band', 'Bars + source band'), T('grouped', 'Grouped')],
    backgrounds: ['none', 'colour'],
    defaultLayout: 'bars', defaultBackground: 'none',
    pairsWith: ['trend', 'kpi', 'stat-row'], fontRole: 'body',
  },
  pricing: {
    purpose: 'Tiers and what each includes.',
    goodForData: '2–4 pricing tiers with features, one highlighted.',
    regions: ['eyebrow', 'title', 'lede', 'footer'],
    itemRegions: ['price-*-name', 'price-*-num', 'price-*-per', 'price-*-note', 'price-*-tag', 'price-*-feat-*', 'pmx-*-label', 'pmx-*-cell-*'],
    layouts: [T('cards', 'Cards'), T('table', 'Feature table'), T('spotlight', 'Spotlight')],
    backgrounds: ['none', 'colour', 'gradient'],
    defaultLayout: 'cards', defaultBackground: 'none',
    pairsWith: ['compare', 'features', 'cta'], fontRole: 'body',
  },
  timeline: {
    purpose: 'A roadmap or sequence of milestones.',
    goodForData: 'Dated milestones with short notes.',
    regions: ['eyebrow', 'title', 'footer'],
    itemRegions: ['tl-*-date', 'tl-*-t', 'tl-*-n'],
    layouts: [T('horizontal', 'Horizontal'), T('vertical', 'Vertical'), T('milestones-band', 'Milestones band')],
    backgrounds: ['none', 'colour', 'gradient'],
    defaultLayout: 'horizontal', defaultBackground: 'none',
    pairsWith: ['agenda', 'features', 'cta'], fontRole: 'body',
  },
  compare: {
    purpose: 'Before/after or option A vs B.',
    goodForData: 'Two columns of contrasting points, one highlighted.',
    regions: ['eyebrow', 'title', 'footer'],
    itemRegions: ['cmp-left-tag', 'cmp-left-title', 'cmp-left-*', 'cmp-right-tag', 'cmp-right-title', 'cmp-right-*'],
    layouts: [T('two-col', 'Two columns'), T('stacked', 'Stacked'), T('versus', 'Versus')],
    backgrounds: ['none', 'colour', 'gradient'],
    defaultLayout: 'two-col', defaultBackground: 'none',
    pairsWith: ['pricing', 'features', 'bullets'], fontRole: 'body',
  },
  features: {
    purpose: 'Product pillars / what you get.',
    goodForData: 'Three (or a few) features with an icon, heading and line.',
    regions: ['eyebrow', 'title', 'footer'],
    itemRegions: ['feat-*-h', 'feat-*-d'],
    layouts: [T('trio', 'Trio'), T('two-col', 'Two columns'), T('with-lede-panel', 'With lede panel'), T('numbered', 'Numbered')],
    backgrounds: ['none', 'colour', 'gradient'],
    defaultLayout: 'trio', defaultBackground: 'none',
    pairsWith: ['split', 'pricing', 'agenda'], fontRole: 'body',
  },
  bullets: {
    purpose: 'A short list of points.',
    goodForData: '3–6 short lines (never paragraphs).',
    regions: ['eyebrow', 'title', 'lede', 'footer'],
    itemRegions: ['bullet-*'],
    layouts: [T('list', 'List'), T('two-col', 'Two columns'), T('with-image', 'With image'), T('numbered', 'Numbered'), T('colour-band', 'Colour band')],
    backgrounds: ['none', 'colour', 'gradient', 'image-split'],
    defaultLayout: 'list', defaultBackground: 'none',
    pairsWith: ['agenda', 'features', 'split'], fontRole: 'body',
  },
  cta: {
    purpose: 'The closing ask / decision.',
    goodForData: 'A headline ask + a short sub + a couple of contact rows.',
    regions: ['eyebrow', 'headline', 'sub'],
    itemRegions: ['contact-*-k', 'contact-*-v'],
    layouts: [T('centred', 'Centred'), T('ink-band', 'Ink band'), T('with-image', 'With image'), T('split-contact', 'Split contact')],
    backgrounds: ['none', 'colour', 'gradient', 'image', 'motion'],
    defaultLayout: 'ink-band', defaultBackground: 'none',
    pairsWith: ['cover', 'stat-row', 'pricing'], fontRole: 'display',
  },
};

/** Layout ids a kind allows (for safeLayout + the picker). */
export function layoutIdsFor(kind: SlideKind): string[] {
  return KIND_META[kind].layouts.map((l) => l.id);
}

// ── The catalogue's validation axes (Phase 5) ─────────────────────────────
// These three tables sit beside KIND_META so the kind contract lives in ONE
// place, and are folded with KIND_META into the generated kind_catalogue.json
// (the TS↔Go source of truth). They are derived from slide-model.ts: do NOT
// hand-edit them out of sync with the interfaces — the TS guard test asserts
// every kind is covered, and the Go validator reads them via the catalogue.

/** Per-kind REQUIRED model fields — the NON-optional members of each kind's
 *  interface in slide-model.ts (over and above the SlideBase id/kind/label that
 *  every slide carries). Absence of any of these is a genuine validation error
 *  (the renderer cannot compile the slide). Mirrors SLIDES.md §5 step 2. */
export const REQUIRED_FIELDS: Record<SlideKind, string[]> = {
  cover: ['title'],
  'full-bleed': [],
  'quote-image': ['quote'],
  quote: ['quote'],
  split: [],
  gallery: ['items'],
  agenda: ['items'],
  'stat-row': ['stats'],
  bignum: ['value', 'vlabel', 'delta'],
  kpi: ['kpis'],
  trend: ['points'],
  chart: ['bars'],
  pricing: ['tiers'],
  timeline: ['milestones'],
  compare: ['left', 'right'],
  features: ['cards'],
  bullets: ['items'],
  cta: ['headline', 'contact'],
};

/** Per-kind primary-array budget — the item count a kind reads well at, mirroring
 *  the "2–4 / 3–6" prompt guidance and the deterministic `budgetV2*` caps in
 *  server/internal/eval/score_v2.go. `field` is the array's model field; `max`
 *  is the overpack cap (over it tends to overflow); `min` is the floor below
 *  which the kind looks thin. Kinds without a primary array are omitted. For
 *  `compare` the budget applies per column (left.items / right.items). */
export interface ArrayBudget { field: string; min: number; max: number }
export const ARRAY_BUDGETS: Partial<Record<SlideKind, ArrayBudget>> = {
  'stat-row': { field: 'stats', min: 2, max: 4 },
  kpi: { field: 'kpis', min: 2, max: 4 },
  bullets: { field: 'items', min: 3, max: 6 },
  agenda: { field: 'items', min: 2, max: 6 },
  features: { field: 'cards', min: 3, max: 4 },
  pricing: { field: 'tiers', min: 2, max: 4 },
  chart: { field: 'bars', min: 2, max: 6 },
  gallery: { field: 'items', min: 2, max: 4 },
  timeline: { field: 'milestones', min: 2, max: 6 },
  compare: { field: 'items', min: 2, max: 6 },
  trend: { field: 'points', min: 2, max: 8 },
};

/** Kinds that legitimately carry NO title region (their headline lives in another
 *  field, or they are a pure statement/media moment). Mirrors the `titleless`
 *  set in server/internal/eval/score_v2.go — a missing title on any OTHER kind
 *  is a soft quality signal. */
export const TITLELESS: SlideKind[] = [
  'cover', 'full-bleed', 'quote-image', 'quote', 'split', 'cta',
];
